import { z } from 'zod';
import { E164PhoneSchema, TimestampSchema, UuidSchema } from './common';
import { PhoneNumberStatusSchema } from './messages';

export const ConversationStatusSchema = z.enum(['open', 'pending', 'closed']);
export type ConversationStatus = z.infer<typeof ConversationStatusSchema>;

export const ConversationDirectionSchema = z.enum(['inbound', 'outbound']);
export type ConversationDirection = z.infer<typeof ConversationDirectionSchema>;

export const ConversationMessageTypeSchema = z.enum(['text', 'image', 'audio', 'document']);
export type ConversationMessageType = z.infer<typeof ConversationMessageTypeSchema>;

export const ConversationMessageDeliveryStatusSchema = z.enum([
  'queued',
  'sent',
  'delivered',
  'read',
  'failed',
]);
export type ConversationMessageDeliveryStatus = z.infer<
  typeof ConversationMessageDeliveryStatusSchema
>;

export const ConversationAuthorTypeSchema = z.enum(['contact', 'agent', 'ai']);
export type ConversationAuthorType = z.infer<typeof ConversationAuthorTypeSchema>;

export const ConversationAssignedToSchema = z.enum(['human', 'ai']).nullable();
export type ConversationAssignedTo = z.infer<typeof ConversationAssignedToSchema>;

export const ConversationMessageSchema = z.object({
  id: UuidSchema,
  conversationId: UuidSchema,
  direction: ConversationDirectionSchema,
  type: ConversationMessageTypeSchema,
  content: z.string(),
  authorType: ConversationAuthorTypeSchema,
  /** Meta media_id (audio/image/etc). UI usa proxy /api/v1/conversations/media/{id}. */
  metaMediaId: z.string().optional(),
  /** MIME type da mídia (audio/ogg, image/jpeg etc). */
  mediaMime: z.string().optional(),
  /** Transcrição via Whisper (apenas inbound audio). NULL = ainda processando ou desabilitado. */
  mediaTranscription: z.string().optional(),
  deliveryStatus: ConversationMessageDeliveryStatusSchema.optional(),
  createdAt: TimestampSchema,
});
export type ConversationMessage = z.infer<typeof ConversationMessageSchema>;

export const ConversationSchema = z.object({
  id: UuidSchema,
  tenantId: UuidSchema,
  contactPhone: E164PhoneSchema,
  contactName: z.string().optional(),
  connectionId: UuidSchema,
  status: ConversationStatusSchema,
  lastMessagePreview: z.string(),
  lastMessageAt: TimestampSchema,
  unreadCount: z.number().int().nonnegative(),
  assignedTo: ConversationAssignedToSchema,
  createdAt: TimestampSchema,
  updatedAt: TimestampSchema,
});
export type Conversation = z.infer<typeof ConversationSchema>;

export const ConversationsListQuerySchema = z.object({
  page: z.coerce.number().int().positive().default(1),
  limit: z.coerce.number().int().min(1).max(100).default(20),
});
export type ConversationsListQuery = z.infer<typeof ConversationsListQuerySchema>;

export const ConversationsListResponseSchema = z.object({
  conversations: z.array(ConversationSchema),
  total: z.number().int().nonnegative(),
  page: z.number().int().positive(),
  limit: z.number().int().positive(),
});
export type ConversationsListResponse = z.infer<typeof ConversationsListResponseSchema>;

export const ConversationDetailSchema = ConversationSchema.extend({
  messages: z.array(ConversationMessageSchema),
  /** Phone number Meta usado na última mensagem outbound dessa conversa.
   * Derivado server-side de `messages.fromPhoneNumberId` via lookup por
   * (connectionId + contactPhone). Null se conversa só teve inbound ou
   * mensagens pre-migration sem `fromPhoneNumberId` rastreado. */
  lastFromPhoneNumberId: z.string().nullable().optional(),
  /** Status do phone vs `connection.phoneNumberIds` atual. */
  lastFromPhoneNumberStatus: PhoneNumberStatusSchema.optional(),
});
export type ConversationDetail = z.infer<typeof ConversationDetailSchema>;

export const SendConversationMessageInputSchema = z.object({
  content: z.string().min(1),
});
export type SendConversationMessageInput = z.infer<typeof SendConversationMessageInputSchema>;
